import { useEffect, useLayoutEffect, useMemo } from 'react';

import { useCustomPagination } from '@/components/shared/hooks/useCustomPagination';

interface UseListPaginationProps<T> {
  items: T[];
  rowsPerPage?: number;
  selectedIndex?: number;
  /**
   * Optional external navigation handler.
   *
   * Called with the first item index of the new page when `onPrev` / `onNext` are triggered.
   * Use it to keep external selection/navigation state in sync (e.g. updating `selectedIndex`, URL params, etc).
   *
   * Note: `selectedIndex` (when provided) will always drive `page` via the `selectedIndex` effect.
   */
  onNavigate?: (firstItemIndex: number) => void;
}

export const useListPagination = <T>({
  items,
  rowsPerPage = 6,
  selectedIndex,
  onNavigate,
}: UseListPaginationProps<T>) => {
  const { maxPages, page, setPage, disablePrev, disableNext, itemsLength } = useCustomPagination({
    filteredResults: items,
    rowsPerPage,
  });

  const pagedItems = useMemo(
    () => items.slice((page - 1) * rowsPerPage, page * rowsPerPage),
    [items, page, rowsPerPage],
  );

  const hasItems = (itemsLength ?? 0) > 0;
  // `useCustomPagination` computes maxPages with `Math.ceil(length / rowsPerPage)`, so for an empty list it returns 0.
  // We still want `onNext` to be a no-op and `disableNext` to be true in that case, even if the UI hides controls.
  const maxPagesSafe = Math.max(1, maxPages);
  const disableNextSafe = !hasItems ? true : disableNext;

  useLayoutEffect(() => {
    setPage(1);
  }, [itemsLength, rowsPerPage, setPage]);

  useEffect(() => {
    if (selectedIndex === undefined || selectedIndex < 0) return;
    setPage(Math.floor(selectedIndex / rowsPerPage) + 1);
  }, [selectedIndex, rowsPerPage, setPage]);

  const onPrev = () => {
    if (page === 1) return;
    const newFirstIndex = (page - 2) * rowsPerPage;
    const newPage = page - 1;
    onNavigate?.(newFirstIndex);
    setPage(newPage);
  };

  const onNext = () => {
    if (disableNextSafe || page >= maxPagesSafe) return;
    const newFirstIndex = page * rowsPerPage;
    const newPage = page + 1;
    onNavigate?.(newFirstIndex);
    setPage(newPage);
  };

  return {
    pagedItems,
    page,
    rowsPerPage,
    onPrev,
    onNext,
    disablePrev,
    disableNext: disableNextSafe,
    itemsLength,
  };
};
